import { FunctionComponent, ReactNode } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { styles } from '../../../../styles/themes/style'
import TooltipWrapper from '../../../../components/TooltipWrapper'
import { ApiKeyInput, useCopyApiKey } from './ApiKeyVisibility'

/* Lucide outlines in currentColor, so each icon takes the colour of what it sits in. */
const Icon = ({ children, size = 16 }: { children: ReactNode, size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ display: 'block', flexShrink: 0 }}>
        {children}
    </svg>
)
export const LayersIcon = () => <Icon size={20}><path d="m12 2 10 5-10 5L2 7l10-5Z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" /></Icon>
export const UserIcon = () => <Icon size={20}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></Icon>
export const UsersIcon = () => <Icon size={20}><circle cx="9" cy="8" r="4" /><path d="M2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1" /><path d="M16 4.13a4 4 0 0 1 0 7.75" /><path d="M22 21v-1a6 6 0 0 0-4-5.65" /></Icon>
const CopyIcon = () => <Icon><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Icon>
const RotateIcon = () => <Icon><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></Icon>
const TrashIcon = () => <Icon><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></Icon>
const InfoIcon = () => <Icon size={14}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></Icon>

interface IApiKeyCardHeaderProps {
    icon: ReactNode
    title: string
    badge?: string
    description: string
    children?: ReactNode
}

/** Icon, title with badge, and description; shared by the key cards and the member key list. */
export const ApiKeyCardHeader: FunctionComponent<IApiKeyCardHeaderProps> = ({ icon, title, badge, description, children }) => (
    <Header>
        <IconCircle>{icon}</IconCircle>
        <HeaderText>
            <TitleRow>
                <Title>{title}</Title>
                {badge && <Badge>{badge}</Badge>}
            </TitleRow>
            <Description>{description}</Description>
        </HeaderText>
        {children}
    </Header>
)

interface IApiKeyCardProps extends Omit<IApiKeyCardHeaderProps, 'children'> {
    apiKey: string | null
    createdAt?: string | null
    /** Display name of who generated the key; left out when it is the viewer's own key. */
    createdBy?: string | null
    lastUsedAt?: string | null
    /** Shown in place of the key when there is none. */
    emptyText: string
    onGenerate: () => void
    onRotate: () => void
    onRevoke: () => void
}

/** One API key: masked field with copy, when it was created and last used, and rotate / revoke. */
const ApiKeyCard: FunctionComponent<IApiKeyCardProps> = ({
    apiKey, createdAt, createdBy, lastUsedAt, emptyText, onGenerate, onRotate, onRevoke, ...header
}) => {
    const copyApiKey = useCopyApiKey()

    return (
        <Card>
            <ApiKeyCardHeader {...header} />
            <Panel>
                {apiKey ? (
                    <>
                        <KeyColumn>
                            <Label>API key</Label>
                            <KeyRow>
                                <ApiKeyInput value={apiKey} />
                                <SquareButton type='button' onClick={() => copyApiKey(apiKey)} aria-label='Copy API key' title='Copy API key'>
                                    <CopyIcon />
                                </SquareButton>
                            </KeyRow>
                        </KeyColumn>
                        <Divider />
                        <Meta>
                            <Label>Created</Label>
                            <MetaValue>{createdAt ? moment(createdAt).format('MMM D, YYYY') : '—'}</MetaValue>
                            {createdBy && <MetaSub>by {createdBy}</MetaSub>}
                        </Meta>
                        <Meta>
                            <Label>Last used</Label>
                            <MetaValue>
                                {lastUsedAt ? moment(lastUsedAt).fromNow() : 'Never'}
                                <TooltipWrapper tooltipText='Updated at most once an hour.'>
                                    <InfoSlot><InfoIcon /></InfoSlot>
                                </TooltipWrapper>
                            </MetaValue>
                        </Meta>
                        <Actions>
                            <ActionButton type='button' onClick={onRotate}><RotateIcon />Rotate key</ActionButton>
                            <ActionButton type='button' $variant='danger' onClick={onRevoke}><TrashIcon />Revoke key</ActionButton>
                        </Actions>
                    </>
                ) : (
                    <>
                        <EmptyText>{emptyText}</EmptyText>
                        <ActionButton type='button' $variant='primary' onClick={onGenerate}>Generate API key</ActionButton>
                    </>
                )}
            </Panel>
        </Card>
    )
}

export const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 14px;
    padding: 22px 24px;
`
const Header = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`
const IconCircle = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${styles.colors.primary100};
    color: ${styles.colors.primary600};
`
const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
`
const TitleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`
const Title = styled.span`
    font-family: NunitoSans;
    font-size: 17px;
    font-weight: 700;
    line-height: 24px;
    color: ${styles.colors.black600};
`
const Badge = styled.span`
    padding: 2px 10px;
    border-radius: 999px;
    background: ${styles.colors.primary100};
    color: ${styles.colors.primary600};
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
`
const Description = styled.span`
    max-width: 640px;
    font-family: NunitoSans;
    font-size: 13px;
    line-height: 20px;
    color: ${styles.colors.black400};
`
const Panel = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px 28px;
    padding: 16px 20px;
    border-radius: 10px;
    background: ${styles.colors.black100};
    border: 1px solid ${styles.colors.uiBackground};
`
/* The labelled columns line up along their labels; the buttons stay centred in the panel. */
const KeyColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 320px;
    min-width: 0;
    align-self: flex-start;
`
const KeyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    input {
        height: 38px;
        border-radius: 8px;
        background: ${styles.colors.white};
        border: 1px solid ${styles.colors.black200};
        color: ${styles.colors.black500};
    }
`
const Label = styled.span`
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 600;
    color: ${styles.colors.black400};
`
const Divider = styled.div`
    align-self: stretch;
    width: 1px;
    background: ${styles.colors.black200};
`
const Meta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 110px;
    align-self: flex-start;
`
const MetaValue = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 600;
    color: ${styles.colors.black600};
    white-space: nowrap;
`
const MetaSub = styled.span`
    font-family: NunitoSans;
    font-size: 12px;
    color: ${styles.colors.black300};
`
const InfoSlot = styled.span`
    display: inline-flex;
    color: ${styles.colors.black300};
    cursor: help;
`
const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
`
const EmptyText = styled.span`
    flex: 1;
    font-family: NunitoSans;
    font-size: 14px;
    color: ${styles.colors.black400};
`
const buttonBase = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 38px;
    border-radius: 8px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.15s ease, border-color 0.15s ease;
    &:focus { outline: none; }
`
const SquareButton = styled.button`
    ${buttonBase}
    width: 38px;
    flex-shrink: 0;
    padding: 0;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    color: ${styles.colors.black500};

    &:hover { border-color: ${styles.colors.primary600}; color: ${styles.colors.primary600}; }
`
const ActionButton = styled.button<{ $variant?: 'default' | 'danger' | 'primary' }>`
    ${buttonBase}
    padding: 0 16px;

    ${({ $variant = 'default' }) => $variant === 'default' && `
        background: ${styles.colors.white};
        border: 1px solid ${styles.colors.black200};
        color: ${styles.colors.black600};
        &:hover { border-color: ${styles.colors.primary600}; color: ${styles.colors.primary600}; }
    `}
    ${({ $variant }) => $variant === 'danger' && `
        background: ${styles.colors.white};
        border: 1px solid ${styles.colors.red400};
        color: ${styles.colors.red600};
        &:hover { background: ${styles.colors.red200}; }
    `}
    ${({ $variant }) => $variant === 'primary' && `
        background: ${styles.colors.primary600};
        border: 1px solid ${styles.colors.primary600};
        color: ${styles.colors.white};
        &:hover { background: ${styles.colors.primary500}; }
    `}
`

export default ApiKeyCard
